// Period arithmetic for the Investment Reporting cycle-creation form.
//
// Pure string/date helpers — no React, no network. Everything here exists because
// create_reporting_cycle(_period_label, _period_type, _params) is FAIL-CLOSED on the
// server (docs/investment-reporting/0003c_reporting_rpcs_and_gate_log.sql §4a) and will
// refuse a cycle whose params are incoherent. Mirroring those refusals client-side turns
// a cryptic Postgres exception into an explicable form error, but the RPC remains the wall.
//
// THREE server-side facts this file mirrors — keep them in step:
//   1. period_type ∈ {'quarterly-full','monthly-interim'}      (0001 CHECK + 0003c guard)
//   2. params.as_of_date, params.window_start, params.window_end are ALL REQUIRED. The
//      compute window is NEVER derived from as_of_date server-side: it defines the
//      trade-date window, the temporal quarantine and the R day-count time-weighting, so
//      it must be a STATED input. (Before 0003c required it, every cycle created without
//      it died at the snapshot stage.) We derive a default here so the human is not asked
//      to hand-type it — but it is still sent explicitly, and still overridable.
//   3. quarterly-full ⇒ window_end MUST EQUAL as_of_date. app.py /reconstruct refuses that
//      mismatch at compute time (as_of_window_end_mismatch), so both the RPC and this form
//      refuse it at creation rather than minting a dead cycle.
//
// PLUS one fact from the OTHER end of the pipeline: the golden-CSV drop point
// (supabase/functions/reporting-golden-ingest/index.ts:1040) constrains period_label to
// /^\d{4}-(Q[1-4]|\d{2})$/ because it becomes a Storage object-key segment AND part of the
// UNIQUE(period_label, kind, content_sha256) drop key. reporting_cycles.period_label itself
// has NO format CHECK, so a cycle CAN be created with a label the ingest will later reject —
// which would silently make the cycle un-droppable. We enforce the ingest's pattern HERE, at
// the point the label is chosen, so the two ends cannot disagree.

import type { CyclePeriod } from "@/types/ir";

// Reuse the IR's own closed domain rather than re-declaring it.
export type ReportingPeriodType = CyclePeriod["type"];

export const PERIOD_TYPES: { value: ReportingPeriodType; label: string; hint: string }[] = [
  {
    value: "monthly-interim",
    label: "Monthly interim",
    hint: "The month just ended. This is the common case — ten months of the year.",
  },
  {
    value: "quarterly-full",
    label: "Quarterly full",
    hint: "The quarter just ended: the full pack, and the webinar month.",
  },
];

// The ingest's own pattern (reporting-golden-ingest/index.ts:1040). Not a guess.
export const PERIOD_LABEL_RX = /^\d{4}-(Q[1-4]|\d{2})$/;

export const PERIOD_LABEL_HELP =
  "YYYY-MM for a month (2026-07) or YYYY-Qn for a quarter (2026-Q2). The golden-CSV drop " +
  "point keys every file on this exact string, so it must be one of those two shapes.";

// ----------------------------------------------------------------------------
// is_webinar_month — DERIVED, never a toggle.
// ----------------------------------------------------------------------------
// Sam's rule, verbatim: "Webinar month will be the same as quarterly month." So this is a
// pure function of period_type and there is deliberately NO checkbox for it anywhere in the
// UI: a checkbox would let the two drift apart, and is_webinar_month is load-bearing — it
// decides whether the webinar-gated views (and therefore the deck kinds the gate demands to
// be frozen before sign-off) are required this cycle. See GateTab's deriveRenderBinding,
// which reads cycle.is_webinar_month to decide exactly that.
export function deriveIsWebinarMonth(periodType: ReportingPeriodType | string): boolean {
  return periodType === "quarterly-full";
}

export const WEBINAR_DERIVATION_NOTE =
  "Derived from the period type, not chosen: the webinar month is the quarterly month, so a " +
  "quarterly-full cycle is a webinar month and a monthly interim never is.";

// ----------------------------------------------------------------------------
// Small date helpers. All string arithmetic in UTC — no Date round-trips on the values we
// emit, so nothing can drift by a day across a timezone boundary.
// ----------------------------------------------------------------------------
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

// Day 0 of month+1 is the last day of month (month1 is 1-based).
function lastDayOfMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

export function isIsoDate(s: string | null | undefined): boolean {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  if (m < 1 || m > 12) return false;
  return d >= 1 && d <= lastDayOfMonth(y, m);
}

// ----------------------------------------------------------------------------
// Defaults that match the business rule:
//   "during a quarter you report the MONTH just ended; after a quarter ends you report the
//    QUARTER."
// So a monthly-interim default is the LAST COMPLETE MONTH, and a quarterly-full default is
// the LAST COMPLETE QUARTER (the quarter BEFORE the one today falls in — the quarter today
// sits in has not finished yet, even on its last day).
// ----------------------------------------------------------------------------
export function suggestPeriodLabel(
  periodType: ReportingPeriodType | string,
  now: Date = new Date(),
): string {
  const y = now.getFullYear();
  const m0 = now.getMonth(); // 0-based, the CURRENT month

  if (periodType === "quarterly-full") {
    const q = Math.floor(m0 / 3) + 1; // the quarter containing today — NOT yet complete
    const prevQ = q === 1 ? 4 : q - 1;
    const prevQy = q === 1 ? y - 1 : y;
    return `${prevQy}-Q${prevQ}`;
  }

  const prevM = m0 === 0 ? 12 : m0; // m0 is 0-based, so m0 == previous month 1-based
  const prevY = m0 === 0 ? y - 1 : y;
  return `${prevY}-${pad2(prevM)}`;
}

export interface PeriodWindow {
  as_of_date: string;
  window_start: string;
  window_end: string;
}

// Derive the compute window + as_of_date from the LABEL (the label is the period identity;
// the type only says which shape of label is legal). Returns null for an unparseable label.
export function derivePeriodWindow(periodLabel: string | null | undefined): PeriodWindow | null {
  const m = (periodLabel ?? "").trim().match(/^(\d{4})-(?:Q([1-4])|(\d{2}))$/);
  if (!m) return null;
  const year = Number(m[1]);

  if (m[2]) {
    const q = Number(m[2]);
    const startM = (q - 1) * 3 + 1;
    const endM = startM + 2;
    const window_start = `${year}-${pad2(startM)}-01`;
    const window_end = `${year}-${pad2(endM)}-${pad2(lastDayOfMonth(year, endM))}`;
    // quarterly: as_of_date MUST equal window_end (0003c refuses otherwise).
    return { as_of_date: window_end, window_start, window_end };
  }

  const month = Number(m[3]);
  if (month < 1 || month > 12) return null;
  const window_start = `${year}-${pad2(month)}-01`;
  const window_end = `${year}-${pad2(month)}-${pad2(lastDayOfMonth(year, month))}`;
  return { as_of_date: window_end, window_start, window_end };
}

// The return datum: the close the period's performance is struck FROM, i.e. the previous
// period's last day. NOT the same as window_start, and the two are easy to conflate —
// window_start is the first day OF the period (it scopes trades, the temporal quarantine
// and the R time-weighting), whereas an FE export for July spans 30 Jun → 31 Jul because
// a July return is measured from the 30 June close.
//
// reporting-golden-ingest derives exactly this itself when it validates the FE preamble —
// `expected_from = isoAddDays(window_start, -1)` (index.ts:1242; see the note at :71,
// "`from` is the PRIOR period end, i.e. window_start − 1 day. Coding `from == window_start`
// false-fails EVERY export"). This mirrors it purely so the create form can SHOW the range
// the file will carry. Nothing is sent to the server from here — moving window_start back a
// day to "fix" the apparent off-by-one would make the ingest expect the day before that,
// and reject a valid export.
export function priorPeriodEnd(windowStart: string | null | undefined): string | null {
  if (!isIsoDate(windowStart ?? "")) return null;
  const [y, m, d] = (windowStart as string).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

// Is this label shaped like a quarter (YYYY-Qn) rather than a month (YYYY-MM)?
export function isQuarterLabel(periodLabel: string | null | undefined): boolean {
  return /^\d{4}-Q[1-4]$/.test((periodLabel ?? "").trim());
}

// ----------------------------------------------------------------------------
// Client-side mirror of create_reporting_cycle's refusals. Returns a list of human problems
// (empty == the RPC's own validations should pass). This never decides anything — the RPC
// re-checks every one of these server-side.
// ----------------------------------------------------------------------------
export interface NewCycleDraft {
  period_type: ReportingPeriodType | string;
  period_label: string;
  as_of_date: string;
  window_start: string;
  window_end: string;
}

export function validateNewCycle(draft: NewCycleDraft): string[] {
  const problems: string[] = [];
  const label = (draft.period_label ?? "").trim();

  if (draft.period_type !== "monthly-interim" && draft.period_type !== "quarterly-full") {
    problems.push("Choose a period type.");
  }
  if (!label) {
    problems.push("Period label is required.");
  } else if (!PERIOD_LABEL_RX.test(label)) {
    problems.push(`Period label "${label}" is not usable. ${PERIOD_LABEL_HELP}`);
  } else if (draft.period_type === "quarterly-full" && !isQuarterLabel(label)) {
    problems.push(
      `A quarterly-full cycle needs a quarter label (e.g. ${suggestPeriodLabel("quarterly-full")}), not "${label}".`,
    );
  } else if (draft.period_type === "monthly-interim" && isQuarterLabel(label)) {
    problems.push(
      `A monthly-interim cycle needs a month label (e.g. ${suggestPeriodLabel("monthly-interim")}), not "${label}".`,
    );
  } else if (!derivePeriodWindow(label)) {
    // The pattern admits YYYY-00 and YYYY-13; the calendar does not.
    problems.push(`"${label}" is not a real month — the month part must be 01 to 12.`);
  }

  if (!isIsoDate(draft.as_of_date)) problems.push("As-of date must be a real YYYY-MM-DD date.");
  if (!isIsoDate(draft.window_start)) problems.push("Window start must be a real YYYY-MM-DD date.");
  if (!isIsoDate(draft.window_end)) problems.push("Window end must be a real YYYY-MM-DD date.");

  if (isIsoDate(draft.window_start) && isIsoDate(draft.window_end) && draft.window_start > draft.window_end) {
    problems.push("Window start is after window end.");
  }
  // The server refuses this outright for quarterly-full: the snapshot horizon (as_of_date)
  // and the time-weighting horizon (window_end) must share an end, or a late-quarter trade
  // lands in one and not the other.
  if (
    draft.period_type === "quarterly-full" &&
    isIsoDate(draft.as_of_date) &&
    isIsoDate(draft.window_end) &&
    draft.window_end !== draft.as_of_date
  ) {
    problems.push(
      "On a quarterly-full cycle the window end must equal the as-of date — the snapshot and " +
        "time-weighting horizons have to share an end.",
    );
  }

  return problems;
}
